/**
 * Код ANZONIX — свой язык индикаторов.
 *
 * Блоки и «Своё условие» закрывают почти всё, но тот, кто привык писать
 * индикаторы кодом, хочет именно код: свои формулы, свои линии, свою
 * логику. Здесь — язык, похожий на запись трейдера в тетради:
 *
 *     // Пересечение средних с фильтром RSI
 *     настройка быстрая = 9 "Быстрая EMA" от 2 до 100
 *     б = ema(закрытие, быстрая)
 *     м = ema(закрытие, 21)
 *     линия б "Быстрая" мята
 *     купить когда пересекает_вверх(б, м) и rsi(14) < 70
 *
 * Русские слова и английские синонимы равноправны (`купить когда` =
 * `buy when`). Простые ряды считаются по всей истории сразу, поэтому
 * `закрытие[1]` — закрытие прошлой свечи, а в будущее заглянуть нельзя:
 * отрицательный сдвиг — ошибка.
 *
 * Для серьёзной логики — память и блоки: «память тренд = 0»,
 * «если … то … иначе … конец», циклы «для» и «пока», свои функции,
 * списки, метки, зоны и отрезки на графике, табло. Такой код идёт по
 * свечам слева направо, как вживую (`script-steps.ts`).
 *
 * Код не исполняется как JavaScript: это собственный разбор и
 * собственный вычислитель с ограничением на число шагов и без доступа к
 * чему-либо, кроме свечей. Поэтому его безопасно считать и в терминале, и
 * на сервере — там, где живут оповещения и боты.
 *
 * Сигнал «купить когда» — момент, когда условие стало верным: на свече,
 * где оно верно, а на прошлой — нет. Иначе «RSI ниже 30» рисовал бы
 * отметку на каждой свече зоны. «купить» внутри блока — событие ровно на
 * той свече, где блок его выполнил.
 */
import { DEFAULT_TRADE_PLAN, emptyBranch, isTimeframe, normalizeTradePlan, TIMEFRAMES } from '@specter/types';
import { childrenOf, Fail, isCondition, isPure, isText, walkSteps, } from './script-ast.js';
import { barsSinceOf, DAY_PERIOD, emaOf, map1, map2, periodIndex, pivotOf, POINT, rsiOf, scriptPeriodOf, series, shift, truth, valueWhenOf, vwapState, vwapStep, WINDOW, windowed } from './script-math.js';
export { scriptPeriodOf } from './script-math.js';
import { EMPTY_DRAWINGS, runSteps } from './script-steps.js';
export { MAX_DRAWINGS, textOfNumber } from './script-steps.js';
/* ================================================================ слова */
/** Слова внутри выражений: имя ими назвать нельзя. */
const KEYWORDS = {
    настройка: 'input',
    input: 'input',
    линия: 'plot',
    plot: 'plot',
    купить: 'buy',
    buy: 'buy',
    продать: 'sell',
    sell: 'sell',
    сообщить: 'alert',
    alert: 'alert',
    фон: 'background',
    background: 'background',
    bgcolor: 'background',
    уровень: 'level',
    level: 'level',
    hline: 'level',
    когда: 'when',
    when: 'when',
    от: 'from',
    from: 'from',
    до: 'to',
    to: 'to',
    и: 'and',
    and: 'and',
    или: 'or',
    or: 'or',
    не: 'not',
    not: 'not',
};
/**
 * Слова, с которых начинается шаг. Узнаются только в начале строки: имя
 * «конец» или «шаг» в выражении остаётся просто именем, и старый код с
 * такими именами не ломается.
 */
const STATEMENTS = {
    если: 'if',
    if: 'if',
    иначе: 'else',
    else: 'else',
    конец: 'end',
    end: 'end',
    для: 'for',
    for: 'for',
    пока: 'while',
    while: 'while',
    память: 'var',
    var: 'var',
    список: 'list',
    list: 'list',
    array: 'list',
    функция: 'function',
    function: 'function',
    func: 'function',
    вернуть: 'return',
    return: 'return',
    прервать: 'break',
    break: 'break',
    дальше: 'continue',
    continue: 'continue',
    табло: 'table',
    table: 'table',
    план: 'plan',
    plan: 'plan',
};
const THEN = new Set(['то', 'then']);
const STEP_WORDS = new Set(['шаг', 'step', 'by']);
/** Типы из Pine после «var»: в нашем коде не нужны, но и не мешают. */
const PINE_TYPES = new Set(['float', 'int', 'bool']);
const ASSIGN_OPS = ['=', ':=', '+=', '-=', '*=', '/='];
const PRICES = {
    закрытие: 'close',
    close: 'close',
    открытие: 'open',
    open: 'open',
    максимум: 'high',
    high: 'high',
    минимум: 'low',
    low: 'low',
    объём: 'volume',
    объем: 'volume',
    volume: 'volume',
    середина: 'hl2',
    hl2: 'hl2',
    типичная: 'hlc3',
    hlc3: 'hlc3',
    час: 'hour',
    hour: 'hour',
    минута: 'minute',
    minute: 'minute',
    день_недели: 'dow',
    dayofweek: 'dow',
    день: 'day',
    dayofmonth: 'day',
    месяц: 'month',
    month: 'month',
    год: 'year',
    year: 'year',
    время: 'time',
    time: 'time',
    бар: 'bar',
    bar_index: 'bar',
    последняя_свеча: 'last',
    is_last: 'last',
};
const CONSTANTS = {
    истина: 1,
    true: 1,
    ложь: 0,
    false: 0,
    пусто: NaN,
    na: NaN,
};
export const COLORS = {
    мята: '#2ee6b0',
    mint: '#2ee6b0',
    зелёный: '#34d399',
    зеленый: '#34d399',
    green: '#34d399',
    красный: '#ff5470',
    red: '#ff5470',
    фиолетовый: '#a78bfa',
    violet: '#a78bfa',
    голубой: '#67e8f9',
    cyan: '#67e8f9',
    синий: '#60a5fa',
    blue: '#60a5fa',
    жёлтый: '#f5c04e',
    желтый: '#f5c04e',
    yellow: '#f5c04e',
    оранжевый: '#fb923c',
    orange: '#fb923c',
    розовый: '#f472b6',
    pink: '#f472b6',
    белый: '#eef2fb',
    white: '#eef2fb',
    серый: '#94a3b8',
    gray: '#94a3b8',
};
/** Встроенные функции: русское имя → каноническое. */
export const FUNCTION_ALIASES = {
    средняя: 'sma',
    эма: 'ema',
    отклонение: 'stdev',
    максимум_за: 'highest',
    минимум_за: 'lowest',
    изменение: 'change',
    сумма: 'sum',
    пересекает_вверх: 'crossup',
    пересекает_вниз: 'crossdown',
    пересекает: 'cross',
    модуль: 'abs',
    больший: 'max',
    меньший: 'min',
    если: 'iff',
    растёт: 'rising',
    растет: 'rising',
    падает: 'falling',
    без_пропусков: 'nz',
    отн_объём: 'relative_volume',
    отн_объем: 'relative_volume',
    корень: 'sqrt',
    степень: 'pow',
    лог: 'log',
    округлить: 'round',
    округлить_вниз: 'floor',
    округлить_вверх: 'ceil',
    знак: 'sign',
    пусто: 'isna',
    na: 'isna',
    свечей_с: 'barssince',
    значение_когда: 'valuewhen',
    свинг_макс: 'pivothigh',
    свинг_мин: 'pivotlow',
    текст: 'tostr',
    vwap_сессии: 'session_vwap',
    vwap_отклонение: 'session_vwap_dev',
    vwap_от: 'anchored_vwap',
    vwap_откл_от: 'anchored_vwap_dev',
    новый_период: 'new_period',
};
export const BUILTINS = {
    sma: { min: 2, max: 2, usage: 'sma(ряд, период)', hint: 'Простое среднее' },
    ema: { min: 2, max: 2, usage: 'ema(ряд, период)', hint: 'Экспоненциальное среднее' },
    wma: { min: 2, max: 2, usage: 'wma(ряд, период)', hint: 'Взвешенное среднее' },
    rsi: { min: 1, max: 2, usage: 'rsi(период) или rsi(ряд, период)', hint: 'RSI, 0–100' },
    atr: { min: 1, max: 1, usage: 'atr(период)', hint: 'Средний ход свечи' },
    stdev: { min: 2, max: 2, usage: 'stdev(ряд, период)', hint: 'Стандартное отклонение' },
    highest: { min: 2, max: 2, usage: 'highest(ряд, период)', hint: 'Наибольшее за период' },
    lowest: { min: 2, max: 2, usage: 'lowest(ряд, период)', hint: 'Наименьшее за период' },
    change: { min: 1, max: 2, usage: 'change(ряд) или change(ряд, свечей)', hint: 'Разница с прошлым' },
    roc: { min: 2, max: 2, usage: 'roc(ряд, период)', hint: 'Изменение в процентах' },
    sum: { min: 2, max: 2, usage: 'sum(ряд, период)', hint: 'Сумма за период' },
    crossup: { min: 2, max: 2, usage: 'crossup(а, б)', hint: 'а пересекает б снизу вверх' },
    crossdown: { min: 2, max: 2, usage: 'crossdown(а, б)', hint: 'а пересекает б сверху вниз' },
    cross: { min: 2, max: 2, usage: 'cross(а, б)', hint: 'Пересечение в любую сторону' },
    abs: { min: 1, max: 1, usage: 'abs(x)', hint: 'Модуль' },
    max: { min: 2, max: 2, usage: 'max(а, б)', hint: 'Большее из двух' },
    min: { min: 2, max: 2, usage: 'min(а, б)', hint: 'Меньшее из двух' },
    iff: { min: 3, max: 3, usage: 'iff(условие, да, нет)', hint: 'Выбор по условию' },
    rising: { min: 2, max: 2, usage: 'rising(ряд, свечей)', hint: 'Выше, чем N свечей назад' },
    falling: { min: 2, max: 2, usage: 'falling(ряд, свечей)', hint: 'Ниже, чем N свечей назад' },
    nz: { min: 1, max: 2, usage: 'nz(x) или nz(x, замена)', hint: 'Пропуск → 0 или замена' },
    sqrt: { min: 1, max: 1, usage: 'sqrt(x)', hint: 'Квадратный корень' },
    pow: { min: 2, max: 2, usage: 'pow(x, степень)', hint: 'Степень числа' },
    log: { min: 1, max: 1, usage: 'log(x)', hint: 'Натуральный логарифм' },
    round: { min: 1, max: 2, usage: 'round(x) или round(x, знаков)', hint: 'Округление' },
    floor: { min: 1, max: 1, usage: 'floor(x)', hint: 'Округление вниз' },
    ceil: { min: 1, max: 1, usage: 'ceil(x)', hint: 'Округление вверх' },
    sign: { min: 1, max: 1, usage: 'sign(x)', hint: 'Знак: 1, 0 или −1' },
    isna: { min: 1, max: 1, usage: 'na(x)', hint: 'Нет ли значения' },
    barssince: { min: 1, max: 1, usage: 'barssince(условие)', hint: 'Сколько свечей прошло с последнего раза, когда условие было верным' },
    valuewhen: { min: 2, max: 3, usage: 'valuewhen(условие, ряд) или valuewhen(условие, ряд, n)', hint: 'Значение ряда, когда условие было верным (n — какой раз с конца, 0 — последний)' },
    pivothigh: { min: 2, max: 2, usage: 'pivothigh(слева, справа)', hint: 'Цена вершины-свинга на свече, где она подтвердилась; иначе пусто' },
    pivotlow: { min: 2, max: 2, usage: 'pivotlow(слева, справа)', hint: 'Цена впадины-свинга на свече, где она подтвердилась; иначе пусто' },
    tostr: { min: 1, max: 2, usage: 'tostr(x) или tostr(x, знаков)', hint: 'Число в текст — для меток и табло' },
    session_vwap: { min: 1, max: 2, usage: 'session_vwap(цена, "D")', hint: 'VWAP с начала дня ("D"), недели ("W"), месяца ("M") или любого периода ("4ч"); время — UTC, как у криптобирж в TradingView' },
    session_vwap_dev: { min: 1, max: 2, usage: 'session_vwap_dev(цена, "D")', hint: 'Отклонение цены от VWAP периода — для полос: vwap + 2 × отклонение' },
    anchored_vwap: { min: 2, max: 2, usage: 'anchored_vwap(цена, условие)', hint: 'VWAP, который начинается заново, когда условие верно (от пробоя, от свинга)' },
    anchored_vwap_dev: { min: 2, max: 2, usage: 'anchored_vwap_dev(цена, условие)', hint: 'Отклонение от VWAP со своим началом — для полос' },
    new_period: { min: 1, max: 1, usage: 'new_period("W")', hint: 'Началась новая свеча периода: день "D", неделя "W" (с понедельника), месяц "M", "4ч"… — как timeframe.change в Pine' },
};
/** Действия со списками. Первый аргумент — всегда список. */
export const LIST_FUNCTIONS = {
    push: { min: 1, max: 1, usage: 'добавить(список, x)', hint: 'Добавить в конец' },
    size: { min: 0, max: 0, usage: 'длина(список)', hint: 'Сколько элементов' },
    get: { min: 1, max: 1, usage: 'взять(список, номер)', hint: 'Элемент по номеру с 0; −1 — последний; нет такого — пусто' },
    set: { min: 2, max: 2, usage: 'заменить(список, номер, x)', hint: 'Записать на место элемента' },
    remove: { min: 1, max: 1, usage: 'убрать(список, номер)', hint: 'Убрать элемент и вернуть его' },
    shift: { min: 0, max: 0, usage: 'убрать_первый(список)', hint: 'Убрать первый и вернуть его' },
    pop: { min: 0, max: 0, usage: 'убрать_последний(список)', hint: 'Убрать последний и вернуть его' },
    clear: { min: 0, max: 0, usage: 'очистить(список)', hint: 'Убрать всё' },
    first: { min: 0, max: 0, usage: 'первый(список)', hint: 'Первый элемент' },
    last: { min: 0, max: 0, usage: 'последний(список)', hint: 'Последний элемент' },
    indexof: { min: 1, max: 1, usage: 'найти(список, x)', hint: 'Номер элемента или −1' },
    includes: { min: 1, max: 1, usage: 'содержит(список, x)', hint: 'Есть ли x в списке' },
    list_max: { min: 0, max: 0, usage: 'наибольшее(список)', hint: 'Наибольший элемент' },
    list_min: { min: 0, max: 0, usage: 'наименьшее(список)', hint: 'Наименьший элемент' },
    list_sum: { min: 0, max: 0, usage: 'сумма_списка(список)', hint: 'Сумма элементов' },
    list_avg: { min: 0, max: 0, usage: 'среднее_списка(список)', hint: 'Среднее элементов' },
};
export const LIST_ALIASES = {
    добавить: 'push',
    push: 'push',
    длина: 'size',
    size: 'size',
    взять: 'get',
    get: 'get',
    заменить: 'set',
    set: 'set',
    убрать: 'remove',
    remove: 'remove',
    убрать_первый: 'shift',
    shift: 'shift',
    убрать_последний: 'pop',
    pop: 'pop',
    очистить: 'clear',
    clear: 'clear',
    первый: 'first',
    first: 'first',
    последний: 'last',
    last: 'last',
    найти: 'indexof',
    indexof: 'indexof',
    содержит: 'includes',
    includes: 'includes',
    наибольшее: 'list_max',
    list_max: 'list_max',
    наименьшее: 'list_min',
    list_min: 'list_min',
    сумма_списка: 'list_sum',
    list_sum: 'list_sum',
    среднее_списка: 'list_avg',
    list_avg: 'list_avg',
};
/** Рисование: сколько чисел нужно; цвет и подпись — в любом месте после них. */
export const DRAW_FUNCTIONS = {
    label: { min: 1, max: 2, usage: 'метка(цена, "текст", цвет, назад)', hint: 'Метка у цены; назад — на сколько свечей левее (0 — эта свеча)' },
    box: { min: 3, max: 4, usage: 'зона(назад, верх, низ, цвет, "текст")', hint: 'Зона от свечи «назад» до последней свечи, пока её не закрыть; четвёртое число — правый край: сколько свечей назад' },
    segment: { min: 4, max: 4, usage: 'отрезок(назад1, цена1, назад2, цена2, цвет, "текст")', hint: 'Отрезок между двумя точками; назад — сколько свечей влево' },
    close: { min: 1, max: 1, usage: 'закрыть(зона)', hint: 'Зона перестаёт тянуться вправо: край — эта свеча' },
    top: { min: 1, max: 1, usage: 'верх_зоны(зона)', hint: 'Верхний край зоны (у метки — её цена, у отрезка — цена начала); удалена — пусто' },
    bottom: { min: 1, max: 1, usage: 'низ_зоны(зона)', hint: 'Нижний край зоны (у метки — её цена, у отрезка — цена конца); удалена — пусто' },
    delete: { min: 1, max: 1, usage: 'удалить(фигура)', hint: 'Убрать метку, зону или отрезок' },
    extend: { min: 1, max: 2, usage: 'продлить(фигура) или продлить(фигура, назад)', hint: 'Правый край зоны или конец отрезка — до этой свечи (или на столько свечей левее)' },
    cell: { min: 2, max: 2, usage: 'ячейка(строка, столбец, "текст", цвет)', hint: 'Ячейка табло, как table.cell в Pine: строки и столбцы с 0, столбец 0 — подпись; видно последнее записанное' },
};
export const DRAW_ALIASES = {
    метка: 'label',
    label: 'label',
    зона: 'box',
    box: 'box',
    zone: 'box',
    отрезок: 'segment',
    segment: 'segment',
    закрыть: 'close',
    close_box: 'close',
    верх_зоны: 'top',
    box_top: 'top',
    низ_зоны: 'bottom',
    box_bottom: 'bottom',
    удалить: 'delete',
    delete: 'delete',
    продлить: 'extend',
    ячейка: 'cell',
    cell: 'cell',
    extend: 'extend',
};
const IDENT_START = /[A-Za-zА-Яа-яЁё_]/;
const IDENT_PART = /[A-Za-zА-Яа-яЁё_0-9]/;
const OPERATORS = [':=', '+=', '-=', '*=', '/=', '<=', '>=', '==', '!=', '<', '>', '+', '-', '*', '/', '%', '(', ')', '[', ']', ',', '=', '.', '?', ':'];
function lex(source) {
    const tokens = [];
    let line = 1;
    let column = 1;
    let i = 0;
    let lineStarted = false;
    const push = (kind, text, at) => {
        tokens.push({ kind, text, line, column: at });
        if (kind !== 'newline')
            lineStarted = true;
    };
    const skipComment = () => {
        while (i < source.length && source[i] !== '\n')
            i += 1;
    };
    while (i < source.length) {
        const char = source[i];
        if (char === '\n') {
            push('newline', '\n', column);
            i += 1;
            line += 1;
            column = 1;
            lineStarted = false;
            continue;
        }
        if (char === ' ' || char === '\t' || char === '\r') {
            i += 1;
            column += 1;
            continue;
        }
        if (char === '/' && source[i + 1] === '/') {
            skipComment();
            continue;
        }
        if (char === '#') {
            // В начале строки «#» — всегда комментарий: цвет строку не начинает.
            const hex = lineStarted ? /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})(?![0-9A-Za-zА-Яа-яЁё_])/.exec(source.slice(i)) : null;
            if (!hex) {
                skipComment();
                continue;
            }
            push('color', hex[0], column);
            i += hex[0].length;
            column += hex[0].length;
            continue;
        }
        if (/[0-9]/.test(char) || (char === '.' && /[0-9]/.test(source[i + 1] ?? ''))) {
            const start = column;
            let text = '';
            while (i < source.length && /[0-9.]/.test(source[i])) {
                text += source[i];
                i += 1;
                column += 1;
            }
            if ((text.match(/\./g) ?? []).length > 1)
                throw new Fail(line, start, `Странное число «${text}»: в числе может быть только одна точка.`);
            // Порядок числа, как в Pine: 1e18, 2.5e-4.
            const exponent = /^[eE][+-]?[0-9]+/.exec(source.slice(i));
            if (exponent) {
                text += exponent[0];
                i += exponent[0].length;
                column += exponent[0].length;
            }
            push('number', text, start);
            continue;
        }
        if (char === '"' || char === '«' || char === "'") {
            const close = char === '«' ? '»' : char;
            const start = column;
            let text = '';
            i += 1;
            column += 1;
            while (i < source.length && source[i] !== close && source[i] !== '\n') {
                text += source[i];
                i += 1;
                column += 1;
            }
            if (source[i] !== close)
                throw new Fail(line, start, `Кавычка не закрыта: нужна «${close}» в конце текста.`);
            i += 1;
            column += 1;
            push('string', text, start);
            continue;
        }
        if (IDENT_START.test(char)) {
            const start = column;
            let text = '';
            while (i < source.length && IDENT_PART.test(source[i])) {
                text += source[i];
                i += 1;
                column += 1;
            }
            push('ident', text.toLowerCase(), start);
            continue;
        }
        const op = OPERATORS.find((candidate) => source.startsWith(candidate, i));
        if (op) {
            push('op', op, column);
            i += op.length;
            column += op.length;
            continue;
        }
        if (char === '&' || char === '|')
            throw new Fail(line, column, `Вместо «${char}${char}» пишется слово: «${char === '&' ? 'и' : 'или'}» (или «${char === '&' ? 'and' : 'or'}»).`);
        if (char === '!')
            throw new Fail(line, column, 'Вместо «!» пишется слово «не» (или «not»); «не равно» — «!=».');
        throw new Fail(line, column, `Непонятный знак «${char}».`);
    }
    push('newline', '\n', column);
    push('eof', '', column);
    return tokens;
}
/* ================================================================ чужие ряды */
/** Слова чужого ряда: таймфрейм или монета. */
const SERIES_WORDS = {
    на: 'timeframe',
    tf: 'timeframe',
    таймфрейм: 'timeframe',
    монета: 'symbol',
    coin: 'symbol',
    symbol: 'symbol',
};
const SESSION_WORDS = new Set(['сессия', 'session']);
/** «4ч», «4h», «4H» — один и тот же таймфрейм. Русские буквы: м, ч, д, н. */
function timeframeOf(text) {
    const raw = text.trim();
    if (isTimeframe(raw))
        return raw;
    const match = /^(\d+)\s*([a-zа-яё]+)$/i.exec(raw);
    if (!match)
        return null;
    const unit = match[2].toLowerCase();
    const letter = ['m', 'м', 'мин'].includes(unit) ? 'm' : ['h', 'ч', 'час'].includes(unit) ? 'H' : ['d', 'д', 'дн', 'день'].includes(unit) ? 'D' : ['w', 'н', 'нед'].includes(unit) ? 'W' : null;
    if (!letter)
        return null;
    const candidate = `${Number(match[1])}${letter}`;
    return isTimeframe(candidate) ? candidate : null;
}
/** «eth», «ETH», «ETH/USDT» — одна монета. Без котировки — к USDT. */
function symbolOf(text) {
    const raw = text.trim().toUpperCase().replace('-', '/');
    const full = raw.includes('/') ? raw : `${raw}/USDT`;
    return /^[A-Z0-9]{2,15}\/[A-Z]{3,5}$/.test(full) ? full : null;
}
/** «08:00-17:00» → минуты от полуночи по UTC. */
function sessionOf(text) {
    const match = /^\s*(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})\s*$/.exec(text);
    if (!match)
        return null;
    const [h1, m1, h2, m2] = match.slice(1).map(Number);
    if (h1 > 24 || h2 > 24 || m1 > 59 || m2 > 59)
        return null;
    return { from: h1 * 60 + m1, to: h2 * 60 + m2 };
}
/* ================================================================ разбор */
const MAX_SOURCE = 200_000;
const MAX_DEPTH = 60;
const MAX_NESTING = 30;
const PLOT_COLORS = ['#a78bfa', '#2ee6b0', '#f5c04e', '#67e8f9', '#f472b6', '#fb923c'];
const STATEMENT_HELP = 'Строка начинается с имени («б = ema(закрытие, 9)», «счёт := счёт + 1») или со слова: настройка, линия, уровень, фон, купить когда, продать когда, сообщить когда, память, список, функция, если, для, пока, табло — или с действия: метка(…), зона(…), добавить(…).';
class Parser {
    tokens;
    registry;
    at = 0;
    depth = 0;
    nesting = 0;
    /** Однострочное «если … то …»: строку может закончить «иначе». */
    inline = false;
    scopes = [new Map()];
    functionList = [];
    /** Имена, которые где-то меняются через «:=», «+=»: такие считаются по свечам с самого начала. */
    reassigned = new Set();
    /** Функция, тело которой разбирается: сама себя она вызвать не может. */
    defining = null;
    draws = false;
    /** Открытые блоки — чтобы подсказать про забытое «конец». */
    openers = [];
    inputs = [];
    assigns = [];
    plots = [];
    backgrounds = [];
    table = [];
    lists = [];
    stepNames = [];
    marks = { buy: false, sell: false, alert: false };
    trade = null;
    buy = null;
    sell = null;
    alert = null;
    constructor(tokens, registry) {
        this.tokens = tokens;
        this.registry = registry;
        for (let k = 0; k + 1 < tokens.length; k += 1) {
            const next = tokens[k + 1];
            if (tokens[k].kind === 'ident' && next.kind === 'op' && ASSIGN_OPS.includes(next.text) && next.text !== '=')
                this.reassigned.add(tokens[k].text);
        }
    }
    peek(offset = 0) {
        return this.tokens[Math.min(this.tokens.length - 1, this.at + offset)];
    }
    next() {
        const token = this.tokens[this.at];
        if (this.at < this.tokens.length - 1)
            this.at += 1;
        return token;
    }
    keyword(token) {
        return token.kind === 'ident' ? KEYWORDS[token.text] ?? null : null;
    }
    /** Слово начала шага — только если это не присваивание имени с таким же написанием. */
    statementWord(offset = 0) {
        const token = this.peek(offset);
        if (token.kind !== 'ident')
            return null;
        const word = STATEMENTS[token.text];
        if (!word)
            return null;
        const after = this.peek(offset + 1);
        if (after.kind === 'op' && ASSIGN_OPS.includes(after.text))
            return null;
        return word;
    }
    isOp(text) {
        const token = this.peek();
        return token.kind === 'op' && token.text === text;
    }
    expectOp(text, what) {
        const token = this.next();
        if (token.kind !== 'op' || token.text !== text)
            throw new Fail(token.line, token.column, `Ожидалось «${text}» ${what}, а здесь ${describe(token)}.`);
        return token;
    }
    atLineEnd() {
        const token = this.peek();
        return token.kind === 'newline' || token.kind === 'eof' || (this.inline && this.statementWord() === 'else');
    }
    endOfStatement() {
        const token = this.peek();
        if (token.kind === 'newline') {
            this.next();
            return;
        }
        if (token.kind === 'eof')
            return;
        if (this.inline && this.statementWord() === 'else')
            return;
        throw new Fail(token.line, token.column, `Лишнее в конце строки: ${describe(token)}. Одна строка — одно утверждение.`);
    }
    /* ------------------------------------------------------------ имена */
    lookup(name) {
        for (let k = this.scopes.length - 1; k >= 0; k -= 1) {
            const found = this.scopes[k].get(name);
            if (found)
                return found;
        }
        return undefined;
    }
    define(token, kind, bind) {
        const name = token.text;
        if (KEYWORDS[name])
            throw new Fail(token.line, token.column, `«${name}» — служебное слово, им нельзя назвать величину.`);
        if (PRICES[name])
            throw new Fail(token.line, token.column, `«${name}» — это цена или время свечи, имя уже занято. Возьмите другое, например «моя_${name}».`);
        if (name in CONSTANTS)
            throw new Fail(token.line, token.column, `«${name}» — постоянная языка. Возьмите другое имя.`);
        if (BUILTINS[FUNCTION_ALIASES[name] ?? name] || this.registry.some((spec) => spec.id === name) || LIST_ALIASES[name] || DRAW_ALIASES[name] || SERIES_WORDS[name] || SESSION_WORDS.has(name)) {
            throw new Fail(token.line, token.column, `«${name}» — имя встроенной функции. Возьмите другое.`);
        }
        const before = this.lookup(name);
        if (before) {
            const hint = before.kind === 'value' ? ` Чтобы изменить значение, пишите «${name} := …».` : '';
            throw new Fail(token.line, token.column, `Имя «${name}» уже задано на строке ${before.line}.${hint}`);
        }
        const binding = { kind, bind, slot: `${name}@${token.line}:${token.column}`, line: token.line };
        this.scopes[this.scopes.length - 1].set(name, binding);
        return binding;
    }
    visibleNames() {
        const out = [];
        for (const scope of this.scopes)
            for (const [name, binding] of scope)
                if (binding.kind === 'value')
                    out.push(name);
        return out;
    }
    /* ------------------------------------------------------------ программа */
    program() {
        const steps = [];
        const ctx = { top: true, fn: false, loops: 0 };
        while (this.peek().kind !== 'eof') {
            if (this.peek().kind === 'newline') {
                this.next();
                continue;
            }
            this.statement(ctx, steps);
        }
        const shows = this.buy || this.sell || this.alert || this.plots.length > 0 || this.backgrounds.length > 0 || this.table.length > 0 || this.marks.buy || this.marks.sell || this.marks.alert || this.draws;
        if (!shows) {
            throw new Fail(1, 1, 'Код ничего не показывает: добавьте «линия …», «фон когда …», «купить когда …», «продать когда …», «сообщить когда …», «табло …» или нарисуйте метку, зону, отрезок.');
        }
        return {
            inputs: this.inputs,
            assigns: this.assigns,
            plots: this.plots,
            backgrounds: this.backgrounds,
            buy: this.buy,
            sell: this.sell,
            alert: this.alert,
            steps,
            functions: this.functionList,
            lists: this.lists,
            table: this.table,
            marks: this.marks,
            stepNames: this.stepNames,
            trade: this.trade,
        };
    }
    onlyTop(ctx, token, example) {
        if (ctx.top)
            return;
        const open = this.openers[this.openers.length - 1];
        const forgot = open ? ` Если блок «${open.text}» со строки ${open.line} должен был закончиться выше — поставьте там «конец».` : '';
        throw new Fail(token.line, token.column, `«${token.text}» пишется вне блоков и функций — в начале строки на верхнем уровне: ${example}${forgot}`);
    }
    statement(ctx, out) {
        const token = this.peek();
        const after = this.peek(1);
        if (token.kind === 'ident' && after.kind === 'op' && ASSIGN_OPS.includes(after.text) && !KEYWORDS[token.text]) {
            this.assignment(ctx, out);
            return;
        }
        const word = this.keyword(token);
        if (word === 'input') {
            this.onlyTop(ctx, token, 'настройка период = 14');
            this.inputStatement(token);
            return;
        }
        if (word === 'plot' || word === 'level') {
            this.onlyTop(ctx, token, 'линия б "Быстрая" мята. Чтобы показывать линию не везде — если(условие, значение, пусто).');
            this.plotStatement(token, word);
            return;
        }
        if (word === 'background') {
            this.onlyTop(ctx, token, 'фон когда условие мята');
            this.backgroundStatement(token);
            return;
        }
        if (word === 'buy' || word === 'sell' || word === 'alert') {
            const side = word;
            if (this.keyword(after) === 'when') {
                if (!ctx.top)
                    throw new Fail(token.line, token.column, `Внутри блока достаточно «${token.text}» без «когда»: событие будет на той свече, где блок дошёл до этой строки.`);
                this.whenStatement(token, side);
                return;
            }
            if (ctx.top) {
                const say = /[a-z]/.test(token.text) ? 'when' : 'когда';
                throw new Fail(after.line, after.column, `После «${token.text}» нужно «${say}»: ${token.text} ${say} условие. Просто «${token.text}» пишется внутри «если … конец».`);
            }
            this.next();
            const text = this.atLineEnd() ? null : this.textual(this.expression());
            this.marks[side] = true;
            out.push({ kind: 'signal', side, text, line: token.line, column: token.column });
            this.endOfStatement();
            return;
        }
        const statement = this.statementWord();
        switch (statement) {
            case 'if':
                this.ifStatement(ctx, out);
                return;
            case 'for':
                this.forStatement(ctx, out);
                return;
            case 'while':
                this.whileStatement(ctx, out);
                return;
            case 'var':
                this.memoryStatement(ctx, out);
                return;
            case 'list': {
                this.onlyTop(ctx, token, 'список уровни');
                this.next();
                const name = this.next();
                if (name.kind !== 'ident')
                    throw new Fail(name.line, name.column, 'После «список» нужно имя: список уровни');
                this.define(name, 'list', 'memory');
                this.lists.push(name.text);
                this.endOfStatement();
                return;
            }
            case 'function':
                this.onlyTop(ctx, token, 'функция размах(а, б) = а - б');
                this.functionStatement(token);
                return;
            case 'return': {
                if (!ctx.fn)
                    throw new Fail(token.line, token.column, '«вернуть» бывает только внутри функции.');
                this.next();
                const expr = this.atLineEnd() ? null : this.numeric(this.expression());
                out.push({ kind: 'return', expr, line: token.line, column: token.column });
                this.endOfStatement();
                return;
            }
            case 'break':
            case 'continue': {
                if (ctx.loops === 0)
                    throw new Fail(token.line, token.column, `«${token.text}» бывает только внутри цикла «для» или «пока».`);
                this.next();
                out.push({ kind: statement, line: token.line, column: token.column });
                this.endOfStatement();
                return;
            }
            case 'table':
                this.onlyTop(ctx, token, 'табло "Тренд" если(т > 0, "вверх", "вниз")');
                this.tableStatement(token);
                return;
            case 'plan':
                this.onlyTop(ctx, token, 'план стоп свинг 0.3 цели 1 2 3 безубыток 1');
                this.planStatement(token);
                return;
            case 'else':
            case 'end':
                throw new Fail(token.line, token.column, `«${token.text}» без начала блока: выше нет «если», «для», «пока» или «функция», которые оно закрывает.`);
            default:
                break;
        }
        if (token.kind === 'ident' && after.kind === 'op' && after.text === '(') {
            const expr = this.expression();
            if (expr.kind !== 'draw' && expr.kind !== 'lcall' && expr.kind !== 'ucall') {
                throw new Fail(token.line, token.column, `Само по себе «${token.text}(…)» ничего не делает. Запомните результат под именем: имя = ${token.text}(…)`);
            }
            out.push({ kind: 'do', expr, line: token.line, column: token.column });
            this.endOfStatement();
            return;
        }
        if (token.kind === 'ident' && !this.lookup(token.text) && !PRICES[token.text]) {
            throw new Fail(token.line, token.column, `Не знаю слова «${token.text}».${suggest(token.text, [...Object.keys(STATEMENTS), ...Object.keys(KEYWORDS)])} ${STATEMENT_HELP}`);
        }
        throw new Fail(token.line, token.column, `${STATEMENT_HELP} Здесь ${describe(token)}.`);
    }
    inputStatement(token) {
        this.next();
        const name = this.next();
        if (name.kind !== 'ident')
            throw new Fail(name.line, name.column, 'После «настройка» нужно имя: настройка период = 14');
        this.define(name, 'value', 'input');
        this.expectOp('=', 'после имени настройки');
        const negative = this.isOp('-') ? (this.next(), -1) : 1;
        const value = this.next();
        if (value.kind !== 'number')
            throw new Fail(value.line, value.column, `Настройка задаётся числом: настройка ${name.text} = 14`);
        const number = negative * Number(value.text);
        let label = name.text;
        let min = number > 0 ? Math.max(1, Math.round(number / 5)) : number * 5;
        let max = number > 0 ? number * 5 : Math.abs(number) * 5 || 100;
        if (this.peek().kind === 'string')
            label = this.next().text;
        if (this.keyword(this.peek()) === 'from') {
            this.next();
            min = this.signedNumber('после «от»');
            const to = this.next();
            if (this.keyword(to) !== 'to')
                throw new Fail(to.line, to.column, 'После «от N» нужно «до M»: настройка период = 14 от 2 до 100');
            max = this.signedNumber('после «до»');
        }
        if (min > max)
            throw new Fail(token.line, token.column, `У настройки «${name.text}» «от» больше «до».`);
        this.inputs.push({ name: name.text, value: Math.min(max, Math.max(min, number)), label, min, max, line: token.line });
        this.endOfStatement();
    }
    plotStatement(token, word) {
        this.next();
        const expr = this.numeric(this.expression());
        // Уровень — из чисел и настроек: «уровень 70», «уровень -край», «уровень 100 - порог».
        const constant = (node) => node.kind === 'num' || (node.kind === 'name' && node.bind === 'input') || ((node.kind === 'unary' || node.kind === 'binary') && childrenOf(node).every(constant));
        if (word === 'level' && !constant(expr)) {
            throw new Fail(token.line, token.column, 'Уровень — это число или настройка: уровень 70 "Перекупленность". Для меняющейся величины есть «линия».');
        }
        let label = word === 'level' ? 'Уровень' : `Линия ${this.plots.length + 1}`;
        if (this.peek().kind === 'string')
            label = this.next().text;
        const color = this.color(word === 'level' ? '#94a3b8' : PLOT_COLORS[this.plots.length % PLOT_COLORS.length]);
        if (this.plots.length >= 12)
            throw new Fail(token.line, token.column, 'Линий и уровней вместе не больше двенадцати: иначе график превращается в клубок.');
        this.plots.push({ expr, label, color, line: token.line, style: word === 'level' ? 'level' : 'line' });
        this.endOfStatement();
    }
    backgroundStatement(token) {
        this.next();
        const when = this.next();
        if (this.keyword(when) !== 'when') {
            const say = /[a-z]/.test(token.text) ? 'when' : 'когда';
            throw new Fail(when.line, when.column, `После «${token.text}» нужно «${say}»: ${token.text} ${say} условие цвет`);
        }
        const expr = this.numeric(this.expression());
        let label = `Фон ${this.backgrounds.length + 1}`;
        if (this.peek().kind === 'string')
            label = this.next().text;
        const color = this.color(PLOT_COLORS[(this.backgrounds.length + 1) % PLOT_COLORS.length]);
        if (this.backgrounds.length >= 4)
            throw new Fail(token.line, token.column, 'Заливок фона не больше четырёх.');
        this.backgrounds.push({ expr, color, label, line: token.line });
        this.endOfStatement();
    }
    whenStatement(token, side) {
        this.next();
        this.next();
        const expr = this.numeric(this.expression());
        if (side === 'alert') {
            const text = this.peek().kind === 'string' ? this.next().text : 'Сработало условие';
            if (this.alert)
                throw new Fail(token.line, token.column, 'Сообщение уже задано выше. Объедините условия через «или».');
            this.alert = { expr, text };
        }
        else if (side === 'buy') {
            if (this.buy)
                throw new Fail(token.line, token.column, 'Условие покупки уже задано выше. Объедините условия через «или».');
            this.buy = expr;
        }
        else {
            if (this.sell)
                throw new Fail(token.line, token.column, 'Условие продажи уже задано выше. Объедините условия через «или».');
            this.sell = expr;
        }
        this.endOfStatement();
    }
    tableStatement(token) {
        this.next();
        const label = this.next();
        if (label.kind !== 'string')
            throw new Fail(label.line, label.column, 'После «табло» — подпись в кавычках: табло "Тренд" если(т > 0, "вверх", "вниз")');
        const expr = this.textual(this.expression());
        const color = this.peek().kind === 'color' || (this.peek().kind === 'ident' && COLORS[this.peek().text]) ? this.color('') : null;
        if (this.table.length >= 12)
            throw new Fail(token.line, token.column, 'Строк табло не больше двенадцати.');
        this.table.push({ label: label.text, expr, color, line: token.line });
        this.endOfStatement();
    }
    /**
     * «план стоп свинг 0.3 цели 1 2 3 доли 50 30 20 безубыток 1 трейлинг атр 2
     * вход откат 50 за 8 время 40 риск 100» — план сделки прямо в коде. Что не
     * сказано — как в плане по умолчанию; потом его можно поменять во вкладке «Сделка».
     */
    planStatement(head) {
        this.next();
        if (this.trade)
            throw new Fail(head.line, head.column, 'План сделки уже задан выше — соберите всё в одну строку «план …».');
        const plan = {};
        const word = () => (this.peek().kind === 'ident' ? this.peek().text : null);
        const number = (what) => {
            const negative = this.isOp('-') ? (this.next(), -1) : 1;
            const token = this.next();
            if (token.kind !== 'number')
                throw new Fail(token.line, token.column, `Ожидалось число ${what}.`);
            return negative * Number(token.text);
        };
        const maybeNumber = () => (this.peek().kind === 'number' ? Number(this.next().text) : null);
        const HELP = 'В плане бывают: стоп (процент, атр, свинг), цели, доли, безубыток, трейлинг (атр, процент, r, свинг), вход (закрытие, откат), время, риск.';
        while (!this.atLineEnd()) {
            const token = this.next();
            const clause = token.kind === 'ident' ? token.text : '';
            if (['стоп', 'stop'].includes(clause)) {
                const mode = this.isOp('%') ? (this.next(), '%') : (this.next().text ?? '');
                if (['процент', 'percent', '%', 'проц'].includes(mode))
                    plan.stop = 'percent';
                else if (['атр', 'atr'].includes(mode))
                    plan.stop = 'atr';
                else if (['свинг', 'swing', 'за_свингом'].includes(mode))
                    plan.stop = 'swing';
                else if (['свеча', 'свечой', 'candle', 'за_свечой'].includes(mode))
                    plan.stop = 'candle';
                else
                    throw new Fail(token.line, token.column, 'После «стоп» — как его ставить: процент, атр, свинг или свеча (за свечой сигнала). Например: стоп атр 1.5');
                const value = maybeNumber();
                if (value !== null)
                    plan.stopValue = value;
                else if (plan.stop === 'candle')
                    plan.stopValue = 0.1;
                else if (plan.stop !== 'swing')
                    throw new Fail(token.line, token.column, `Сколько? Например: стоп ${mode} ${plan.stop === 'percent' ? 2 : 1.5}`);
                if (['не_дальше', 'max'].includes(word() ?? '')) {
                    this.next();
                    plan.maxStopAtr = number('после «не_дальше» — в ATR');
                }
            }
            else if (['цели', 'цель', 'targets', 'target'].includes(clause)) {
                const values = [];
                while (this.peek().kind === 'number')
                    values.push(Number(this.next().text));
                if (values.length === 0 || values.length > 5)
                    throw new Fail(token.line, token.column, 'Цели — от одной до пяти, в R (размерах стопа): цели 1 2 3');
                plan.targets = values.map((r) => ({ r, share: Math.round((100 / values.length) * 100) / 100 }));
            }
            else if (['доли', 'shares'].includes(clause)) {
                const values = [];
                while (this.peek().kind === 'number')
                    values.push(Number(this.next().text));
                if (!plan.targets || values.length !== plan.targets.length)
                    throw new Fail(token.line, token.column, 'Доли — после целей и по одной на каждую цель: цели 1 2 3 доли 50 30 20');
                plan.targets = plan.targets.map((target, n) => ({ ...target, share: values[n] }));
            }
            else if (['безубыток', 'breakeven'].includes(clause)) {
                plan.breakevenAfter = Math.round(number('— после какой цели: безубыток 1'));
            }
            else if (['трейлинг', 'trail', 'trailing'].includes(clause)) {
                const mode = this.isOp('%') ? (this.next(), '%') : this.next().text;
                const modes = { атр: 'atr', atr: 'atr', процент: 'percent', percent: 'percent', '%': 'percent', r: 'r', ступенями: 'r', свинг: 'swing', swing: 'swing' };
                const trail = modes[mode];
                if (!trail)
                    throw new Fail(token.line, token.column, 'После «трейлинг» — как тянуть стоп: атр, процент, r или свинг. Например: трейлинг атр 2');
                const value = trail === 'swing' ? (maybeNumber() ?? 0.3) : number(`после «трейлинг ${mode}»`);
                let afterProfit = true;
                if (['сразу', 'always'].includes(word() ?? '')) {
                    this.next();
                    afterProfit = false;
                }
                plan.trail = { mode: trail, value, afterProfit };
            }
            else if (['вход', 'entry'].includes(clause)) {
                const mode = this.next().text;
                if (['закрытие', 'close'].includes(mode))
                    plan.entry = 'close';
                else if (['откат', 'retest'].includes(mode)) {
                    plan.entry = 'retest';
                    const depth = maybeNumber();
                    if (depth !== null)
                        plan.retestDepth = depth;
                    if (['за', 'within'].includes(word() ?? '')) {
                        this.next();
                        plan.retestBars = Math.round(number('— сколько свечей ждать отката'));
                    }
                }
                else
                    throw new Fail(token.line, token.column, 'После «вход» — закрытие или откат: вход откат 50 за 8');
            }
            else if (['время', 'time', 'bars'].includes(clause)) {
                plan.maxBars = Math.round(number('— через сколько свечей закрыть сделку'));
            }
            else if (['риск', 'risk'].includes(clause)) {
                plan.riskMoney = number('— риск на сделку в долларах');
            }
            else if (['параллельно', 'overlap'].includes(clause)) {
                plan.oneAtATime = false;
            }
            else {
                throw new Fail(token.line, token.column, `Не знаю «${token.text}» в плане сделки. ${HELP}`);
            }
        }
        if (Object.keys(plan).length === 0)
            throw new Fail(head.line, head.column, `План пустой. ${HELP}`);
        this.trade = plan;
        this.endOfStatement();
    }
    assignment(ctx, out) {
        const name = this.next();
        const op = this.next();
        if (op.text === '=') {
            const expr = this.numeric(this.expression());
            if (ctx.top && !this.reassigned.has(name.text) && isPure(expr)) {
                this.define(name, 'value', 'series');
                this.assigns.push({ name: name.text, expr, line: name.line });
            }
            else {
                const binding = this.define(name, 'value', ctx.fn ? 'local' : 'bar');
                out.push({ kind: 'assign', name: name.text, slot: binding.slot, local: ctx.fn, declare: true, op: '=', expr, line: name.line, column: name.column });
                if (ctx.top)
                    this.stepNames.push({ name: name.text, slot: binding.slot, line: name.line, yes: isCondition(expr) });
            }
            this.endOfStatement();
            return;
        }
        const binding = this.lookup(name.text);
        if (!binding) {
            throw new Fail(name.line, name.column, `Имя «${name.text}» ещё не задано. Сначала задайте его: память ${name.text} = 0 — тогда значение будет переходить со свечи на свечу.`);
        }
        if (binding.kind !== 'value')
            throw new Fail(name.line, name.column, `«${name.text}» — ${binding.kind === 'list' ? 'список: его меняют добавить(…), заменить(…), убрать(…)' : 'функция, её нельзя перезаписать'}.`);
        if (binding.bind === 'input')
            throw new Fail(name.line, name.column, `«${name.text}» — настройка: её меняет ползунок, а не код. Заведите своё имя: моё = ${name.text}.`);
        const expr = this.numeric(this.expression());
        const kind = op.text === ':=' ? '=' : op.text[0];
        out.push({ kind: 'assign', name: name.text, slot: binding.slot, local: binding.bind === 'local', declare: false, op: kind, expr, line: name.line, column: name.column });
        this.endOfStatement();
    }
    memoryStatement(ctx, out) {
        const token = this.next();
        if (ctx.fn)
            throw new Fail(token.line, token.column, 'Память внутри функции не поддерживается: заведите её вне функции, а функция может её менять через «:=».');
        if (this.peek().kind === 'ident' && PINE_TYPES.has(this.peek().text) && this.peek(1).kind === 'ident')
            this.next();
        const name = this.next();
        if (name.kind !== 'ident')
            throw new Fail(name.line, name.column, 'После «память» нужно имя: память тренд = 0');
        this.expectOp('=', `после «память ${name.text}» — начальное значение: память ${name.text} = 0`);
        const init = this.numeric(this.expression());
        const binding = this.define(name, 'value', 'memory');
        out.push({ kind: 'memory', name: name.text, slot: binding.slot, local: false, init, line: name.line, column: name.column });
        if (ctx.top)
            this.stepNames.push({ name: name.text, slot: binding.slot, line: name.line, yes: false });
        this.endOfStatement();
    }
    /** Блок до одного из слов `stops` (не съедая его). */
    block(ctx, stops, opener) {
        this.nesting += 1;
        if (this.nesting > MAX_NESTING)
            throw new Fail(opener.line, opener.column, 'Слишком глубокая вложенность блоков. Вынесите часть в функцию.');
        this.scopes.push(new Map());
        this.openers.push(opener);
        const body = [];
        for (;;) {
            const token = this.peek();
            if (token.kind === 'eof')
                throw new Fail(opener.line, opener.column, `«${opener.text}» на строке ${opener.line} не закрыто: в конце блока нужно «конец».`);
            if (token.kind === 'newline') {
                this.next();
                continue;
            }
            const word = this.statementWord();
            if (word && stops.includes(word))
                break;
            this.statement(ctx, body);
        }
        this.scopes.pop();
        this.openers.pop();
        this.nesting -= 1;
        return body;
    }
    /** «конец», за которым можно написать, что закрывается: «конец если». */
    closeBlock() {
        this.next();
        const token = this.peek();
        if (token.kind === 'ident' && ['if', 'for', 'while', 'function'].includes(STATEMENTS[token.text] ?? ''))
            this.next();
        this.endOfStatement();
    }
    condition() {
        return this.numeric(this.expression());
    }
    optionalThen() {
        const token = this.peek();
        if (token.kind === 'ident' && THEN.has(token.text)) {
            this.next();
            return true;
        }
        return false;
    }
    /** Одна строка после «то» или «иначе» в однострочном «если». */
    inlineBody(ctx) {
        const body = [];
        const saved = this.inline;
        this.inline = true;
        this.scopes.push(new Map());
        const word = this.statementWord();
        if (word === 'if' || word === 'for' || word === 'while' || word === 'function') {
            const token = this.peek();
            throw new Fail(token.line, token.column, `В однострочном «если … то …» — одно простое действие. Для «${token.text}» разверните блок на несколько строк с «конец».`);
        }
        this.statement(ctx, body);
        this.scopes.pop();
        this.inline = saved;
        return body;
    }
    ifStatement(ctx, out) {
        const head = this.next();
        const inner = { top: false, fn: ctx.fn, loops: ctx.loops };
        const branches = [];
        let otherwise = null;
        const when = this.condition();
        if (this.optionalThen() && !this.atLineEnd()) {
            branches.push({ when, body: this.inlineBody(inner) });
            if (this.statementWord() === 'else') {
                this.next();
                otherwise = this.inlineBody(inner);
            }
            out.push({ kind: 'if', branches, otherwise, line: head.line, column: head.column });
            return;
        }
        this.endOfStatement();
        branches.push({ when, body: this.block(inner, ['else', 'end'], head) });
        for (;;) {
            if (this.statementWord() === 'end') {
                this.closeBlock();
                break;
            }
            this.next();
            if (this.statementWord() === 'if') {
                this.next();
                const next = this.condition();
                this.optionalThen();
                this.endOfStatement();
                branches.push({ when: next, body: this.block(inner, ['else', 'end'], head) });
                continue;
            }
            this.endOfStatement();
            otherwise = this.block(inner, ['end'], head);
            this.closeBlock();
            break;
        }
        out.push({ kind: 'if', branches, otherwise, line: head.line, column: head.column });
    }
    forStatement(ctx, out) {
        const head = this.next();
        const name = this.next();
        if (name.kind !== 'ident')
            throw new Fail(name.line, name.column, 'После «для» нужно имя счётчика: для i от 0 до 10');
        const separator = this.next();
        if (this.keyword(separator) !== 'from' && !(separator.kind === 'op' && separator.text === '=')) {
            throw new Fail(separator.line, separator.column, `После «для ${name.text}» нужно «от»: для ${name.text} от 0 до 10`);
        }
        const from = this.numeric(this.expression());
        const to = this.next();
        if (this.keyword(to) !== 'to')
            throw new Fail(to.line, to.column, `В цикле нужно «до»: для ${name.text} от 0 до 10`);
        const until = this.numeric(this.expression());
        let step = null;
        if (this.peek().kind === 'ident' && STEP_WORDS.has(this.peek().text)) {
            this.next();
            step = this.numeric(this.expression());
        }
        this.endOfStatement();
        this.scopes.push(new Map());
        const binding = this.define(name, 'value', 'local');
        const body = this.block({ top: false, fn: ctx.fn, loops: ctx.loops + 1 }, ['end'], head);
        this.scopes.pop();
        this.closeBlock();
        out.push({ kind: 'for', slot: binding.slot, from, to: until, step, body, line: head.line, column: head.column });
    }
    whileStatement(ctx, out) {
        const head = this.next();
        const when = this.condition();
        this.endOfStatement();
        const body = this.block({ top: false, fn: ctx.fn, loops: ctx.loops + 1 }, ['end'], head);
        this.closeBlock();
        out.push({ kind: 'while', when, body, line: head.line, column: head.column });
    }
    functionStatement(head) {
        this.next();
        const name = this.next();
        if (name.kind !== 'ident')
            throw new Fail(name.line, name.column, 'После «функция» нужно имя: функция размах(а, б) = а - б');
        if (this.lookup(name.text))
            throw new Fail(name.line, name.column, `Имя «${name.text}» уже задано на строке ${this.lookup(name.text).line}.`);
        this.expectOp('(', `после имени функции: функция ${name.text}(а, б)`);
        const params = [];
        if (!this.isOp(')')) {
            for (;;) {
                const param = this.next();
                if (param.kind !== 'ident')
                    throw new Fail(param.line, param.column, `Параметры функции — имена через запятую: функция ${name.text}(а, б)`);
                params.push(param);
                if (this.isOp(',')) {
                    this.next();
                    continue;
                }
                break;
            }
        }
        this.expectOp(')', 'в конце списка параметров');
        this.scopes.push(new Map());
        const slots = params.map((param) => this.define(param, 'value', 'local').slot);
        this.defining = name.text;
        let body;
        if (this.isOp('=')) {
            const token = this.next();
            const expr = this.numeric(this.expression());
            body = [{ kind: 'return', expr, line: token.line, column: token.column }];
            this.endOfStatement();
        }
        else {
            this.endOfStatement();
            body = this.block({ top: false, fn: true, loops: 0 }, ['end'], head);
            this.closeBlock();
        }
        this.defining = null;
        this.scopes.pop();
        this.define(name, 'function', 'local');
        this.functionList.push({ name: name.text, params: params.map((param) => param.text), slots, body, line: head.line });
    }
    /** Цвет после подписи: слово («мята») или #hex. Нет — остаётся предложенный. */
    color(fallback) {
        const tone = this.peek();
        if (tone.kind === 'color')
            return this.next().text;
        if (tone.kind === 'ident' && !KEYWORDS[tone.text] && !(this.inline && STATEMENTS[tone.text] === 'else')) {
            const known = COLORS[tone.text];
            if (!known)
                throw new Fail(tone.line, tone.column, `Не знаю цвета «${tone.text}». Есть: мята, фиолетовый, голубой, жёлтый, красный, зелёный, розовый, оранжевый, белый — или #ff5470.`);
            this.next();
            return known;
        }
        return fallback;
    }
    signedNumber(what) {
        const negative = this.isOp('-') ? (this.next(), -1) : 1;
        const token = this.next();
        if (token.kind !== 'number')
            throw new Fail(token.line, token.column, `Ожидалось число ${what}.`);
        return negative * Number(token.text);
    }
    /* ------------------------------------------------------------ числа и текст */
    /** Выражение должно быть числом или условием — не текстом и не цветом. */
    numeric(expr) {
        const check = (node) => {
            if (node.kind === 'text')
                throw new Fail(node.line, node.column, 'Текст здесь не к месту: он бывает подписью — в метке, табло или сообщении.');
            if (node.kind === 'color')
                throw new Fail(node.line, node.column, 'Цвет здесь не к месту: он пишется в линии, фоне, табло, метке, зоне или отрезке.');
            if (node.kind === 'call' && node.name === 'tostr')
                throw new Fail(node.line, node.column, 'текст(…) превращает число в подпись — в расчётах он не нужен.');
            if (node.kind === 'draw')
                return;
            childrenOf(node).forEach(check);
        };
        check(expr);
        return expr;
    }
    /** Подпись: текст, число, «+» между ними, выбор «если» между подписями. */
    textual(expr) {
        if (expr.kind === 'text')
            return expr;
        if (expr.kind === 'call' && expr.name === 'tostr') {
            expr.args.forEach((arg) => this.numeric(arg));
            return expr;
        }
        if (expr.kind === 'binary' && expr.op === '+' && isText(expr)) {
            this.textual(expr.left);
            this.textual(expr.right);
            return expr;
        }
        if (expr.kind === 'call' && expr.name === 'iff' && isText(expr)) {
            this.numeric(expr.args[0]);
            this.textual(expr.args[1]);
            this.textual(expr.args[2]);
            return expr;
        }
        return this.numeric(expr);
    }
    /* ------------------------------------------------------------ выражения */
    expression() {
        this.depth += 1;
        if (this.depth > MAX_DEPTH) {
            const token = this.peek();
            throw new Fail(token.line, token.column, 'Слишком глубокая вложенность скобок. Разбейте выражение на несколько имён.');
        }
        let result = this.or();
        if (this.isOp('?')) {
            const token = this.next();
            const yes = this.expression();
            this.expectOp(':', 'в выборе «условие ? да : нет»');
            const no = this.expression();
            result = { kind: 'call', name: 'iff', args: [result, yes, no], line: token.line, column: token.column };
        }
        this.depth -= 1;
        return result;
    }
    or() {
        let left = this.and();
        while (this.keyword(this.peek()) === 'or') {
            const token = this.next();
            left = { kind: 'binary', op: 'or', left, right: this.and(), line: token.line, column: token.column };
        }
        return left;
    }
    and() {
        let left = this.not();
        while (this.keyword(this.peek()) === 'and') {
            const token = this.next();
            left = { kind: 'binary', op: 'and', left, right: this.not(), line: token.line, column: token.column };
        }
        return left;
    }
    not() {
        if (this.keyword(this.peek()) === 'not') {
            const token = this.next();
            return { kind: 'unary', op: 'not', of: this.not(), line: token.line, column: token.column };
        }
        return this.compare();
    }
    compare() {
        const left = this.add();
        const token = this.peek();
        if (token.kind === 'op' && ['<', '>', '<=', '>=', '==', '!='].includes(token.text)) {
            this.next();
            return { kind: 'binary', op: token.text, left, right: this.add(), line: token.line, column: token.column };
        }
        if (token.kind === 'op' && token.text === '=')
            throw new Fail(token.line, token.column, 'В условии равенство пишется «==». Одно «=» — это задать имя.');
        return left;
    }
    add() {
        let left = this.mul();
        while (this.isOp('+') || this.isOp('-')) {
            const token = this.next();
            left = { kind: 'binary', op: token.text, left, right: this.mul(), line: token.line, column: token.column };
        }
        return left;
    }
    mul() {
        let left = this.unary();
        while (this.isOp('*') || this.isOp('/') || this.isOp('%')) {
            const token = this.next();
            left = { kind: 'binary', op: token.text, left, right: this.unary(), line: token.line, column: token.column };
        }
        return left;
    }
    unary() {
        if (this.isOp('-')) {
            const token = this.next();
            return { kind: 'unary', op: '-', of: this.unary(), line: token.line, column: token.column };
        }
        return this.postfix();
    }
    postfix() {
        let expr = this.primary();
        for (;;) {
            if (this.isOp('[')) {
                const token = this.next();
                if (this.isOp('-'))
                    throw new Fail(token.line, token.column, 'В будущее смотреть нельзя: сдвиг пишется положительным, закрытие[1] — прошлая свеча.');
                const first = this.peek();
                if (first.kind === 'number' && this.peek(1).kind === 'op' && this.peek(1).text === ']') {
                    this.next();
                    if (!Number.isInteger(Number(first.text)))
                        throw new Fail(first.line, first.column, 'В скобках — целое число свечей назад: закрытие[1].');
                    if (Number(first.text) > 5000)
                        throw new Fail(first.line, first.column, 'Назад можно смотреть не дальше 5000 свечей.');
                    this.next();
                    expr = { kind: 'index', of: expr, back: Number(first.text), line: token.line, column: token.column };
                    continue;
                }
                const back = this.numeric(this.expression());
                this.expectOp(']', 'после числа свечей');
                expr = { kind: 'at', of: expr, back, line: token.line, column: token.column };
                continue;
            }
            if (this.isOp('.')) {
                const token = this.next();
                const name = this.next();
                if (name.kind !== 'ident')
                    throw new Fail(name.line, name.column, 'После точки — имя линии индикатора: bollinger(20, 2).upper');
                expr = { kind: 'member', of: expr, name: name.text, line: token.line, column: token.column };
                continue;
            }
            return expr;
        }
    }
    args(what) {
        const args = [];
        if (!this.isOp(')')) {
            for (;;) {
                args.push(this.expression());
                if (this.isOp(',')) {
                    this.next();
                    continue;
                }
                break;
            }
        }
        this.expectOp(')', `в конце вызова ${what}(…)`);
        return args;
    }
    primary() {
        const token = this.next();
        if (token.kind === 'number')
            return { kind: 'num', value: Number(token.text), line: token.line, column: token.column };
        if (token.kind === 'op' && token.text === '(') {
            const expr = this.expression();
            this.expectOp(')', 'чтобы закрыть скобку');
            return expr;
        }
        if (token.kind === 'string')
            return { kind: 'text', value: token.text, line: token.line, column: token.column };
        if (token.kind === 'color')
            return { kind: 'color', value: token.text, line: token.line, column: token.column };
        if (token.kind === 'ident') {
            if (KEYWORDS[token.text])
                throw new Fail(token.line, token.column, `Слово «${token.text}» здесь не к месту: ожидалось число, имя или функция.`);
            const binding = this.lookup(token.text);
            if (this.isOp('('))
                return this.callOf(token, binding);
            if (binding) {
                if (binding.kind === 'list')
                    throw new Fail(token.line, token.column, `«${token.text}» — список. Его элементы: взять(${token.text}, 0), последний(${token.text}), длина(${token.text}).`);
                if (binding.kind === 'function')
                    throw new Fail(token.line, token.column, `«${token.text}» — функция, её вызывают со скобками: ${token.text}(…)`);
                return { kind: 'name', name: token.text, bind: binding.bind, slot: binding.slot, line: token.line, column: token.column };
            }
            if (PRICES[token.text])
                return { kind: 'name', name: token.text, bind: 'price', line: token.line, column: token.column };
            if (token.text in CONSTANTS)
                return { kind: 'num', value: CONSTANTS[token.text], line: token.line, column: token.column };
            if (COLORS[token.text])
                return { kind: 'color', value: COLORS[token.text], line: token.line, column: token.column };
            const known = [...Object.keys(PRICES), ...this.visibleNames()];
            throw new Fail(token.line, token.column, `Не знаю имени «${token.text}».${suggest(token.text, known)} Имя нужно задать выше: ${token.text} = …`);
        }
        if (token.kind === 'newline' || token.kind === 'eof')
            throw new Fail(token.line, token.column, 'Строка оборвалась: выражение не закончено.');
        throw new Fail(token.line, token.column, `Здесь ожидалось число, имя или функция, а стоит ${describe(token)}.`);
    }
    callOf(token, binding) {
        const at = { line: token.line, column: token.column };
        if (binding?.kind === 'function') {
            this.next();
            const args = this.args(token.text).map((arg) => this.numeric(arg));
            const fn = this.functionList.find((entry) => entry.name === token.text);
            if (args.length !== fn.params.length) {
                throw new Fail(token.line, token.column, `${token.text} ждёт ${fn.params.length} ${plural(fn.params.length, 'значение', 'значения', 'значений')} (${fn.params.join(', ')}), а получила ${args.length}.`);
            }
            return { kind: 'ucall', name: token.text, args, ...at };
        }
        if (binding)
            throw new Fail(token.line, token.column, `«${token.text}» — не функция, а величина: скобки после неё не нужны.`);
        if (this.defining === token.text)
            throw new Fail(token.line, token.column, 'Функция не может вызывать сама себя: перепишите через цикл «для» или «пока».');
        if (SERIES_WORDS[token.text])
            return this.otherSeries(token);
        const periodic = FUNCTION_ALIASES[token.text] ?? token.text;
        if (periodic === 'new_period' || periodic === 'session_vwap' || periodic === 'session_vwap_dev') {
            // Период пишется текстом: "D", "W", "M", "4ч" — его разбираем сразу, в расчёт идёт число.
            this.next();
            const period = (text) => {
                const code = text.kind === 'string' ? scriptPeriodOf(text.text) : null;
                if (code === null)
                    throw new Fail(text.line, text.column, `Период пишется в кавычках: "D" — день, "W" — неделя, "M" — месяц, "4ч", "15м", "60".`);
                return { kind: 'num', value: code, line: text.line, column: text.column };
            };
            if (periodic === 'new_period') {
                const code = period(this.next());
                this.expectOp(')', `в конце ${token.text}(…)`);
                return { kind: 'call', name: 'new_period', args: [code], ...at };
            }
            const price = this.numeric(this.expression());
            let code = { kind: 'num', value: DAY_PERIOD, ...at };
            if (this.isOp(',')) {
                this.next();
                code = period(this.next());
            }
            this.expectOp(')', `в конце ${token.text}(…)`);
            const anchor = { kind: 'call', name: 'new_period', args: [code], ...at };
            return { kind: 'call', name: periodic === 'session_vwap' ? 'anchored_vwap' : 'anchored_vwap_dev', args: [price, anchor], ...at };
        }
        if (SESSION_WORDS.has(token.text)) {
            this.next();
            const text = this.next();
            const session = text.kind === 'string' ? sessionOf(text.text) : null;
            if (!session)
                throw new Fail(text.line, text.column, 'Сессия пишется в кавычках по UTC: сессия("08:00-17:00").');
            this.expectOp(')', 'в конце сессии(…)');
            return { kind: 'session', from: session.from, to: session.to, ...at };
        }
        const list = LIST_ALIASES[token.text];
        if (list) {
            this.next();
            const target = this.next();
            const bound = target.kind === 'ident' ? this.lookup(target.text) : undefined;
            if (bound?.kind !== 'list')
                throw new Fail(target.line, target.column, `Первым в ${token.text}(…) идёт список, заданный выше: список уровни, потом ${LIST_FUNCTIONS[list].usage}.`);
            const rest = [];
            if (this.isOp(',')) {
                this.next();
                rest.push(...this.args(token.text));
            }
            else
                this.expectOp(')', `в конце ${token.text}(…)`);
            const spec = LIST_FUNCTIONS[list];
            if (rest.length < spec.min || rest.length > spec.max)
                throw new Fail(token.line, token.column, `Как вызывать: ${spec.usage}.`);
            return { kind: 'lcall', name: list, list: target.text, args: rest.map((arg) => this.numeric(arg)), ...at };
        }
        const draw = DRAW_ALIASES[token.text];
        if (draw) {
            this.next();
            const spec = DRAW_FUNCTIONS[draw];
            let color = null;
            let text = null;
            const nums = [];
            for (const arg of this.args(token.text)) {
                if (arg.kind === 'color') {
                    if (color)
                        throw new Fail(arg.line, arg.column, 'Цвет уже задан.');
                    color = arg.value;
                }
                else if (isText(arg)) {
                    if (text)
                        throw new Fail(arg.line, arg.column, 'Подпись уже задана.');
                    text = this.textual(arg);
                }
                else
                    nums.push(this.numeric(arg));
            }
            if (nums.length < spec.min || nums.length > spec.max)
                throw new Fail(token.line, token.column, `Как вызывать: ${spec.usage}.`);
            if (draw === 'label' || draw === 'box' || draw === 'segment' || draw === 'cell')
                this.draws = true;
            return { kind: 'draw', name: draw, args: nums, color, text, ...at };
        }
        // Имя проверяется до скобок: опечатка в имени полезнее, чем «не закрыта скобка» в конце строки.
        const name = FUNCTION_ALIASES[token.text] ?? token.text;
        const builtin = BUILTINS[name];
        const spec = this.registry.find((entry) => entry.id === name);
        if (!builtin && !spec) {
            const known = [...Object.keys(BUILTINS), ...Object.keys(FUNCTION_ALIASES), ...this.registry.map((entry) => entry.id), ...Object.keys(LIST_ALIASES), ...Object.keys(DRAW_ALIASES), ...this.functionList.map((fn) => fn.name)];
            throw new Fail(token.line, token.column, `Не знаю функции «${token.text}».${suggest(token.text, known)}`);
        }
        this.next();
        const args = this.args(token.text);
        if (builtin && (args.length < builtin.min || args.length > builtin.max)) {
            throw new Fail(token.line, token.column, `${token.text} ждёт ${builtin.min === builtin.max ? builtin.min : `${builtin.min}–${builtin.max}`} ${plural(builtin.max, 'значение', 'значения', 'значений')}, а получила ${args.length}. Как вызывать: ${builtin.usage}.`);
        }
        if (!builtin && spec && args.length > spec.params.length) {
            throw new Fail(token.line, token.column, `${spec.id} принимает не больше ${spec.params.length} ${plural(spec.params.length, 'настройки', 'настроек', 'настроек')}: ${spec.params.map((param) => param.label.toLowerCase()).join(', ') || 'без настроек'}.`);
        }
        if (name === 'iff' || name === 'tostr')
            return { kind: 'call', name, args, ...at };
        if ((!builtin && spec) || name === 'atr' || name === 'pivothigh' || name === 'pivotlow' || (name === 'rsi' && args.length === 1)) {
            for (const arg of args) {
                this.numeric(arg);
                if (!isPure(arg))
                    throw new Fail(arg.line, arg.column, `Настройки ${!builtin && spec ? spec.id : token.text}(…) — числа или настройки: величины из памяти сюда не подходят.`);
            }
        }
        return { kind: 'call', name, args: args.map((arg) => this.numeric(arg)), ...at };
    }
    otherSeries(token) {
        const what = SERIES_WORDS[token.text];
        this.next();
        const name = this.next();
        if (name.kind !== 'string') {
            throw new Fail(name.line, name.column, what === 'timeframe' ? `Таймфрейм пишется в кавычках: ${token.text}("4H", закрытие)` : `Монета пишется в кавычках: ${token.text}("ETH/USDT", закрытие)`);
        }
        let timeframe = null;
        let symbol = null;
        if (what === 'timeframe') {
            timeframe = timeframeOf(name.text);
            if (!timeframe)
                throw new Fail(name.line, name.column, `Не знаю таймфрейма «${name.text}». Есть: ${TIMEFRAMES.join(', ')} — или по-русски: 15м, 4ч, 1д, 1н.`);
        }
        else {
            symbol = symbolOf(name.text);
            if (!symbol)
                throw new Fail(name.line, name.column, `Не понимаю монету «${name.text}». Пишите так: "ETH/USDT" или просто "ETH".`);
        }
        this.expectOp(',', `после ${what === 'timeframe' ? 'таймфрейма' : 'монеты'}: ${token.text}("${name.text}", выражение)`);
        const of = this.numeric(this.expression());
        if (!isPure(of)) {
            throw new Fail(of.line, of.column, `Внутри ${token.text}(…) — только цены, настройки и функции от них: память, циклы и списки считаются по своему графику.`);
        }
        this.expectOp(')', `в конце ${token.text}(…)`);
        return { kind: 'on', symbol, timeframe, of, line: token.line, column: token.column };
    }
}
function describe(token) {
    if (token.kind === 'newline' || token.kind === 'eof')
        return 'конец строки';
    if (token.kind === 'string')
        return `текст «${token.text}»`;
    return `«${token.text}»`;
}
function plural(count, one, few, many) {
    const tens = count % 100;
    const ones = count % 10;
    if (tens >= 11 && tens <= 14)
        return many;
    if (ones === 1)
        return one;
    if (ones >= 2 && ones <= 4)
        return few;
    return many;
}
/** «Может быть, ema?» — ближайшее известное имя, если опечатка небольшая. */
function suggest(word, known) {
    let best = '';
    let bestDistance = Infinity;
    for (const candidate of known) {
        const distance = levenshtein(word, candidate);
        if (distance < bestDistance) {
            best = candidate;
            bestDistance = distance;
        }
    }
    return best && bestDistance <= Math.max(1, Math.floor(word.length / 3)) ? ` Может быть, «${best}»?` : '';
}
function levenshtein(a, b) {
    const row = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
        let previous = row[0];
        row[0] = i;
        for (let j = 1; j <= b.length; j += 1) {
            const current = row[j];
            row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
            previous = current;
        }
    }
    return row[b.length];
}
const compiled = new Map();
/**
 * Код из TradingView узнаём сразу: иначе человек получает ошибку про
 * длину или про незнакомое слово и не понимает, что дело в языке.
 */
const PINE = /^\s*\/\/\s*@version\s*=|^\s*(?:indicator|strategy|study|library)\s*\(|\b(?:ta|input|request)\.[a-z_]+\s*\(/m;
export const PINE_TEXT = 'Это Pine Script из TradingView. Нажмите «Перенести в Код ANZONIX» — студия переведёт его и покажет, что перенести не удалось.';
/** Строка, по которой видно Pine Script; 0 — это не он. */
function pineLine(source) {
    // Комментарии не в счёт: перенесённый код хранит непереносимые строки Pine комментариями.
    const lines = source.split('\n');
    for (let k = 0; k < lines.length; k += 1) {
        const line = lines[k];
        if (/^\s*\/\/\s*@version\s*=/.test(line))
            return k + 1;
        const code = line.replace(/\/\/.*$/, '').replace(/^\s*#.*$/, '');
        if (PINE.test(code))
            return k + 1;
    }
    return 0;
}
/** Разобрать код. Ошибка — по-русски, со строкой и местом. Разобранное запоминается. */
export function compileScript(source, registry) {
    const known = compiled.get(source);
    if (known)
        return known;
    let result;
    try {
        const pine = pineLine(source);
        if (pine > 0)
            throw new Fail(pine, 1, PINE_TEXT);
        if (source.length > MAX_SOURCE)
            throw new Fail(1, 1, `Код длиннее ${MAX_SOURCE.toLocaleString('ru-RU')} знаков — разбейте идею на несколько индикаторов.`);
        const program = new Parser(lex(source), registry).program();
        result = { ok: true, program };
    }
    catch (error) {
        result = error instanceof Fail ? { ok: false, error: { line: error.line, column: error.column, message: error.message } } : { ok: false, error: { line: 1, column: 1, message: 'Код не удалось разобрать.' } };
    }
    if (compiled.size > 200)
        compiled.clear();
    compiled.set(source, result);
    return result;
}
class Runner {
    bars;
    resolve;
    registry;
    context;
    outer;
    length;
    env = new Map();
    /** Чужие ряды, которых не оказалось: код честно молчит там, а редактор говорит почему. */
    missing;
    constructor(bars, resolve, registry, context = {}, 
    /** Внутри «на(…)»/«монета(…)»: величины своего графика сюда не переносятся. */
    outer = null, missing) {
        this.bars = bars;
        this.resolve = resolve;
        this.registry = registry;
        this.context = context;
        this.outer = outer;
        this.length = bars.length;
        this.missing = missing ?? new Set();
    }
    /** Числа (настройки) переходят во вложенный расчёт; ряды своего графика — нет. */
    scalars() {
        return [...this.env].filter((entry) => typeof entry[1] === 'number');
    }
    other(node) {
        const base = this.context.base;
        if (!base) {
            throw new Fail(node.line, node.column, 'Здесь неизвестно, на какой монете и таймфрейме считается код, — «на(…)» и «монета(…)» работают в индикаторе на графике.');
        }
        const want = { venue: base.venue, symbol: node.symbol ?? base.symbol, timeframe: node.timeframe ?? base.timeframe };
        if (want.symbol === base.symbol && want.timeframe === base.timeframe)
            return this.eval(node.of);
        const label = `${want.symbol} · ${want.timeframe}`;
        const other = this.context.series?.(want) ?? null;
        if (!other || other.length === 0) {
            this.missing.add(label);
            return new Float64Array(this.length).fill(NaN);
        }
        // Цены свечи («закрытие», «объём») внутри берутся с чужого ряда; переносить нельзя только свои величины.
        const names = new Set([...(this.outer ?? []), ...[...this.env].filter(([name, value]) => typeof value !== 'number' && !PRICES[name]).map(([name]) => name)]);
        const inner = new Runner(other, this.resolve, this.registry, { ...this.context, base: want }, names, this.missing);
        for (const [name, value] of this.scalars())
            inner.set(name, value);
        const values = series(inner.eval(node.of), other.length);
        /*
         * На каждой своей свече — последний чужой бар, закрывшийся не позже неё.
         * Сравниваются закрытия, а не открытия: ETH на той же часовой свече
         * закрывается вместе с ней — сдвигать его на свечу назад незачем. А
         * четырёхчасовой бар виден только с последней часовой свечи внутри
         * него: раньше он ещё не закрыт. В будущее не заглядывает ни то ни другое.
         */
        const step = other.length > 1 ? other[1].time - other[0].time : 0;
        const own = this.length > 1 ? this.bars[1].time - this.bars[0].time : 0;
        const out = new Float64Array(this.length).fill(NaN);
        let cursor = -1;
        for (let i = 0; i < this.length; i += 1) {
            const closes = this.bars[i].time + own;
            while (cursor + 1 < other.length && other[cursor + 1].time + step <= closes)
                cursor += 1;
            if (cursor >= 0)
                out[i] = values[cursor];
        }
        return out;
    }
    set(name, value) {
        this.env.set(name, value);
    }
    value(name) {
        return this.env.get(name) ?? NaN;
    }
    price(name) {
        const key = PRICES[name];
        const out = new Float64Array(this.length);
        for (let i = 0; i < this.length; i += 1) {
            const bar = this.bars[i];
            out[i] = priceOf(bar, key, i, this.length);
        }
        return out;
    }
    scalar(expr, what) {
        const value = this.eval(expr);
        if (typeof value !== 'number')
            throw new Fail(expr.line, expr.column, `${what} должен быть числом или настройкой, а не рядом.`);
        return value;
    }
    eval(expr) {
        const n = this.length;
        switch (expr.kind) {
            case 'num':
                return expr.value;
            case 'on':
                return this.other(expr);
            case 'session': {
                const out = new Float64Array(n);
                for (let i = 0; i < n; i += 1) {
                    const date = new Date(this.bars[i].time);
                    const minute = date.getUTCHours() * 60 + date.getUTCMinutes();
                    out[i] = (expr.from <= expr.to ? minute >= expr.from && minute < expr.to : minute >= expr.from || minute < expr.to) ? 1 : 0;
                }
                return out;
            }
            case 'name': {
                const known = this.env.get(expr.name);
                if (known !== undefined)
                    return known;
                if (this.outer?.has(expr.name)) {
                    throw new Fail(expr.line, expr.column, `«${expr.name}» посчитано по своему графику — внутри «на(…)» или «монета(…)» его нет. Напишите выражение целиком внутри скобок: на("4H", ema(закрытие, 50)).`);
                }
                if (PRICES[expr.name]) {
                    const value = this.price(expr.name);
                    this.env.set(expr.name, value);
                    return value;
                }
                throw new Fail(expr.line, expr.column, `Не знаю имени «${expr.name}».`);
            }
            case 'index':
                return shift(this.eval(expr.of), expr.back, n);
            case 'unary': {
                const value = this.eval(expr.of);
                return expr.op === '-' ? map1(value, n, (x) => -x) : map1(value, n, (x) => (truth(x) ? 0 : 1));
            }
            case 'binary': {
                const a = this.eval(expr.left);
                const b = this.eval(expr.right);
                switch (expr.op) {
                    case '+':
                        return map2(a, b, n, (x, y) => x + y);
                    case '-':
                        return map2(a, b, n, (x, y) => x - y);
                    case '*':
                        return map2(a, b, n, (x, y) => x * y);
                    case '/':
                        return map2(a, b, n, (x, y) => (y === 0 ? NaN : x / y));
                    case '%':
                        return map2(a, b, n, (x, y) => (y === 0 ? NaN : x % y));
                    case '<':
                        return map2(a, b, n, (x, y) => (x < y ? 1 : 0));
                    case '>':
                        return map2(a, b, n, (x, y) => (x > y ? 1 : 0));
                    case '<=':
                        return map2(a, b, n, (x, y) => (x <= y ? 1 : 0));
                    case '>=':
                        return map2(a, b, n, (x, y) => (x >= y ? 1 : 0));
                    case '==':
                        return map2(a, b, n, (x, y) => (x === y ? 1 : 0));
                    case '!=':
                        return map2(a, b, n, (x, y) => (Number.isFinite(x) && Number.isFinite(y) && x !== y ? 1 : 0));
                    case 'and':
                        return map2(a, b, n, (x, y) => (truth(x) && truth(y) ? 1 : 0));
                    case 'or':
                        return map2(a, b, n, (x, y) => (truth(x) || truth(y) ? 1 : 0));
                    default:
                        throw new Fail(expr.line, expr.column, `Неизвестное действие «${expr.op}».`);
                }
            }
            case 'member': {
                if (expr.of.kind !== 'call' || BUILTINS[expr.of.name])
                    throw new Fail(expr.line, expr.column, 'Точка бывает только после индикатора с несколькими линиями: bollinger(20, 2).upper');
                return this.indicator(expr.of, expr.name);
            }
            case 'call':
                return BUILTINS[expr.name] ? this.builtin(expr) : this.indicator(expr, undefined);
            default:
                throw new Fail(expr.line, expr.column, 'Это выражение считается по свечам — здесь оно не к месту.');
        }
    }
    indicator(call, output) {
        const spec = this.registry.find((entry) => entry.id === call.name);
        if (output !== undefined && !spec.outputs?.some((entry) => entry.key === output)) {
            const list = spec.outputs?.map((entry) => `.${entry.key} (${entry.label.toLowerCase()})`).join(', ');
            throw new Fail(call.line, call.column, list ? `У ${spec.id} нет линии «${output}». Есть: ${list}.` : `У ${spec.id} одна линия — точка не нужна.`);
        }
        if (output === undefined && spec.outputs && spec.outputs.length > 1) {
            // Без точки — первая линия, как в реестре. Подсказка — в справочнике.
            output = spec.outputs[0].key;
        }
        const params = {};
        call.args.forEach((arg, index) => {
            params[spec.params[index].key] = this.scalar(arg, `${spec.params[index].label} у ${spec.id}`);
        });
        for (const param of spec.params)
            if (params[param.key] === undefined)
                params[param.key] = param.default;
        const values = this.resolve(spec.id, params, output, this.bars, this.context);
        const out = new Float64Array(this.length);
        for (let i = 0; i < this.length; i += 1) {
            const value = values[i];
            out[i] = value === null || value === undefined ? NaN : value;
        }
        return out;
    }
    builtin(call) {
        const n = this.length;
        const arg = (index) => this.eval(call.args[index]);
        const ser = (index) => series(arg(index), n);
        const period = (index) => {
            const value = this.scalar(call.args[index], 'Период');
            if (!Number.isFinite(value) || value < 1 || value > 2000)
                throw new Fail(call.line, call.column, `Период у ${call.name} должен быть от 1 до 2000, а получился ${value}.`);
            return Math.floor(value);
        };
        const window = WINDOW[call.name];
        if (window)
            return windowed(ser(0), period(1), window);
        switch (call.name) {
            case 'ema':
                return emaOf(ser(0), period(1));
            case 'rsi':
                return call.args.length === 1 ? rsiOf(this.price('close'), period(0)) : rsiOf(ser(0), period(1));
            case 'atr': {
                const p = period(0);
                const tr = new Float64Array(n);
                for (let i = 0; i < n; i += 1) {
                    const bar = this.bars[i];
                    const previous = this.bars[i - 1];
                    tr[i] = previous ? Math.max(bar.high - bar.low, Math.abs(bar.high - previous.close), Math.abs(bar.low - previous.close)) : bar.high - bar.low;
                }
                return windowed(tr, p, WINDOW['sma']);
            }
            case 'change': {
                const back = call.args.length > 1 ? period(1) : 1;
                const a = arg(0);
                return map2(a, shift(a, back, n), n, (x, y) => x - y);
            }
            case 'roc': {
                const back = period(1);
                const a = arg(0);
                return map2(a, shift(a, back, n), n, (x, y) => (y === 0 ? NaN : ((x - y) / y) * 100));
            }
            case 'rising':
            case 'falling': {
                const back = period(1);
                const a = arg(0);
                return map2(a, shift(a, back, n), n, (x, y) => (call.name === 'rising' ? (x > y ? 1 : 0) : x < y ? 1 : 0));
            }
            case 'crossup':
            case 'crossdown':
            case 'cross': {
                const a = ser(0);
                const b = ser(1);
                const out = new Float64Array(n);
                for (let i = 1; i < n; i += 1) {
                    const up = a[i] > b[i] && a[i - 1] <= b[i - 1];
                    const down = a[i] < b[i] && a[i - 1] >= b[i - 1];
                    out[i] = (call.name === 'crossup' ? up : call.name === 'crossdown' ? down : up || down) ? 1 : 0;
                }
                return out;
            }
            case 'iff': {
                const condition = series(arg(0), n);
                const yes = series(arg(1), n);
                const no = series(arg(2), n);
                const out = new Float64Array(n);
                for (let i = 0; i < n; i += 1)
                    out[i] = truth(condition[i]) ? yes[i] : no[i];
                return out;
            }
            case 'nz': {
                const replacement = call.args.length > 1 ? this.scalar(call.args[1], 'Замена') : 0;
                return map1(arg(0), n, (x) => (Number.isFinite(x) ? x : replacement));
            }
            case 'barssince':
                return barsSinceOf(ser(0));
            case 'new_period': {
                const code = this.scalar(call.args[0], 'Период');
                const out = new Float64Array(n);
                for (let i = 1; i < n; i += 1)
                    out[i] = periodIndex(this.bars[i].time, code) !== periodIndex(this.bars[i - 1].time, code) ? 1 : 0;
                return out;
            }
            case 'anchored_vwap':
            case 'anchored_vwap_dev': {
                const price = ser(0);
                const anchor = ser(1);
                const out = new Float64Array(n);
                const state = vwapState();
                for (let i = 0; i < n; i += 1)
                    out[i] = vwapStep(state, price[i], this.bars[i].volume, truth(anchor[i]), call.name === 'anchored_vwap_dev');
                return out;
            }
            case 'valuewhen':
                return valueWhenOf(ser(0), ser(1), call.args.length > 2 ? this.scalar(call.args[2], 'Номер раза') : 0);
            case 'pivothigh':
            case 'pivotlow': {
                const high = call.name === 'pivothigh';
                return pivotOf(this.price(high ? 'high' : 'low'), period(0), period(1), high);
            }
            default: {
                const point = POINT[call.name];
                if (!point)
                    throw new Fail(call.line, call.column, `Не знаю функции «${call.name}».`);
                const values = call.args.map((_, index) => arg(index));
                if (values.every((value) => typeof value === 'number'))
                    return point(values);
                const columns = values.map((value) => series(value, n));
                const out = new Float64Array(n);
                for (let i = 0; i < n; i += 1)
                    out[i] = point(columns.map((column) => column[i]));
                return out;
            }
        }
    }
}
function priceOf(bar, key, index, length) {
    switch (key) {
        case 'hl2':
            return (bar.high + bar.low) / 2;
        case 'hlc3':
            return (bar.high + bar.low + bar.close) / 3;
        case 'bar':
            return index;
        case 'last':
            return index === length - 1 ? 1 : 0;
        case 'time':
            return bar.time;
        case 'hour':
            return new Date(bar.time).getUTCHours();
        case 'minute':
            return new Date(bar.time).getUTCMinutes();
        case 'dow':
            return ((new Date(bar.time).getUTCDay() + 6) % 7) + 1;
        case 'day':
            return new Date(bar.time).getUTCDate();
        case 'month':
            return new Date(bar.time).getUTCMonth() + 1;
        case 'year':
            return new Date(bar.time).getUTCFullYear();
        default:
            return bar[key];
    }
}
/** Момент, когда условие стало верным: здесь верно, на прошлой свече — нет. */
function edges(value, length) {
    const s = series(value, length);
    const out = new Float64Array(length);
    for (let i = 0; i < length; i += 1)
        out[i] = truth(s[i]) && !(i > 0 && truth(s[i - 1])) && i > 0 ? 1 : 0;
    return out;
}
/** Событие стороны: момент, когда условие стало верным, или «купить» из блока на этой свече. */
function signalOf(condition, marks, length) {
    if (!condition && !marks)
        return null;
    const out = condition ? edges(condition, length) : new Float64Array(length);
    if (marks)
        for (let i = 0; i < length; i += 1)
            if (marks[i])
                out[i] = 1;
    return out;
}
const runs = new WeakMap();
/**
 * Посчитать код на свечах. `inputs` — значения настроек поверх заданных
 * в коде (их двигают ползунками). Ошибка вычисления — тоже по-русски.
 */
export function runScript(source, bars, resolve, registry, inputs = {}, context = {}) {
    const parsed = compileScript(source, registry);
    if (!parsed.ok)
        return parsed;
    // Код с чужими рядами не запоминается по свечам: чужой ряд обновляется сам по себе.
    const remember = scriptRequestsOf(parsed.program).length === 0;
    const key = `${source}\u0000${JSON.stringify(inputs)}\u0000${context.base ? `${context.base.symbol}:${context.base.timeframe}` : ''}`;
    let memory = runs.get(bars);
    if (!memory) {
        memory = new Map();
        runs.set(bars, memory);
    }
    const known = remember ? memory.get(key) : undefined;
    if (known)
        return known;
    let result;
    try {
        const program = parsed.program;
        const runner = new Runner(bars, resolve, registry, context);
        for (const input of program.inputs) {
            const raw = inputs[input.name];
            const value = typeof raw === 'number' && Number.isFinite(raw) ? Math.min(input.max, Math.max(input.min, raw)) : input.value;
            runner.set(input.name, value);
        }
        const length = bars.length;
        const values = [];
        for (const input of program.inputs)
            values.push({ name: input.name, line: input.line, yes: false, values: series(runner.value(input.name), length) });
        for (const assign of program.assigns) {
            const value = runner.eval(assign.expr);
            runner.set(assign.name, value);
            values.push({ name: assign.name, line: assign.line, yes: isCondition(assign.expr), values: series(value, length) });
        }
        const outputs = [...program.plots.map((plot) => plot.expr), ...program.backgrounds.map((entry) => entry.expr), program.buy, program.sell, program.alert?.expr ?? null];
        const stepped = program.steps.length > 0 || program.table.length > 0 || program.marks.buy || program.marks.sell || program.marks.alert || outputs.some((expr) => expr !== null && !isPure(expr));
        let run;
        if (!stepped) {
            const plots = program.plots.map((plot) => ({ label: plot.label, color: plot.color, values: series(runner.eval(plot.expr), length), style: plot.style }));
            const backgrounds = program.backgrounds.map((background) => ({ label: background.label, color: background.color, values: map1(series(runner.eval(background.expr), length), length, (x) => (truth(x) ? 1 : 0)) }));
            const buy = program.buy ? series(runner.eval(program.buy), length) : null;
            const sell = program.sell ? series(runner.eval(program.sell), length) : null;
            const alert = program.alert ? series(runner.eval(program.alert.expr), length) : null;
            run = {
                buy: buy ? edges(buy, length) : null,
                sell: sell ? edges(sell, length) : null,
                alert: alert ? edges(alert, length) : null,
                alertText: program.alert?.text ?? '',
                plots,
                backgrounds,
                values,
                conditions: { buy, sell, alert },
                missing: [...runner.missing],
                drawings: EMPTY_DRAWINGS,
                table: [],
            };
        }
        else {
            const walked = runSteps(program, bars, (expr) => runner.eval(expr));
            for (const entry of program.stepNames)
                values.push({ name: entry.name, line: entry.line, yes: entry.yes, values: walked.slots.get(entry.slot) ?? new Float64Array(length).fill(NaN) });
            values.sort((a, b) => a.line - b.line);
            const marked = (mark, used) => (used ? mark : null);
            const conditionOf = (condition, mark) => condition ?? mark;
            const buyMarks = marked(walked.marks.buy, program.marks.buy);
            const sellMarks = marked(walked.marks.sell, program.marks.sell);
            const alertMarks = marked(walked.marks.alert, program.marks.alert);
            run = {
                buy: signalOf(walked.buy, buyMarks, length),
                sell: signalOf(walked.sell, sellMarks, length),
                alert: signalOf(walked.alert, alertMarks, length),
                alertText: program.alert?.text ?? walked.alertText,
                plots: program.plots.map((plot, k) => ({ label: plot.label, color: plot.color, values: walked.plots[k], style: plot.style })),
                backgrounds: program.backgrounds.map((entry, k) => ({ label: entry.label, color: entry.color, values: walked.backgrounds[k] })),
                values,
                conditions: { buy: conditionOf(walked.buy, buyMarks), sell: conditionOf(walked.sell, sellMarks), alert: conditionOf(walked.alert, alertMarks) },
                missing: [...runner.missing],
                drawings: walked.drawings,
                table: walked.table,
            };
        }
        result = { ok: true, run };
    }
    catch (error) {
        result = error instanceof Fail ? { ok: false, error: { line: error.line, column: error.column, message: error.message } } : { ok: false, error: { line: 1, column: 1, message: 'Код не удалось посчитать.' } };
    }
    if (memory.size > 50)
        memory.clear();
    memory.set(key, result);
    return result;
}
/** Выходы-события кода: покупка, продажа, сообщение. Линии `plotN` — величины. */
export const SCRIPT_EVENTS = { buy: 'покупка', sell: 'продажа', alert: 'сообщение' };
/** Событие ли это кода: `script` с выходом покупки, продажи или сообщения. */
export function isScriptEvent(indicator, output) {
    return indicator === 'script' && (output === undefined || output in SCRIPT_EVENTS);
}
/** Настройки кода из параметров индикатора: ключи вида `$имя`. */
export function scriptInputsOf(params) {
    const out = {};
    for (const [key, value] of Object.entries(params))
        if (key.startsWith('$') && typeof value === 'number')
            out[key.slice(1)] = value;
    return out;
}
/**
 * Ряд для движка: `buy`, `sell`, `alert` — единицы в моменты сигналов;
 * `plot0`… — значения линий. Ошибка кода — пустой ряд: индикатор молчит,
 * а не врёт (сама ошибка видна в редакторе).
 */
export function scriptSeries(params, output, bars, resolve, registry, context = {}) {
    const source = typeof params['source'] === 'string' ? params['source'] : '';
    const result = runScript(source, bars, resolve, registry, scriptInputsOf(params), context);
    if (!result.ok)
        return bars.map(() => null);
    const run = result.run;
    let values = null;
    if (output === 'sell')
        values = run.sell;
    else if (output === 'alert')
        values = run.alert;
    else if (output?.startsWith('plot'))
        values = run.plots[Number(output.slice(4))]?.values ?? null;
    else
        values = run.buy;
    if (!values)
        return bars.map(() => null);
    return Array.from(values, (value) => (Number.isFinite(value) ? value : null));
}
/* ================================================================ индикатор из кода */
/** Операнд-выход кода для условий движка. */
export function scriptOperand(source, name, output, inputs = {}) {
    const params = { source, name };
    for (const [key, value] of Object.entries(inputs))
        params[`$${key}`] = value;
    return { kind: 'indicator', indicator: 'script', params, output };
}
/**
 * Обычное описание индикатора из кода: ветвь покупки, если есть
 * «купить когда» (или «купить» в блоке), ветвь продажи — если есть
 * продажа, а одно сообщение без сторон — предупреждение. Дальше это
 * такой же индикатор, как собранный из блоков: к нему можно добавить фон
 * и усилители, поставить оповещение, отдать боту.
 */
export function specFromScript(source, frame, registry, inputs = {}) {
    const parsed = compileScript(source, registry);
    if (!parsed.ok)
        return null;
    const program = parsed.program;
    const happened = (output) => ({ left: scriptOperand(source, frame.name, output, inputs), operator: 'above', right: { kind: 'number', value: 0 } });
    const branches = [];
    if (program.buy || program.marks.buy)
        branches.push({ ...emptyBranch('LONG'), trigger: happened('buy') });
    if (program.sell || program.marks.sell)
        branches.push({ ...emptyBranch('SHORT'), trigger: happened('sell') });
    /*
     * Код, который только рисует (зоны, метки, линии, табло), — тоже
     * индикатор: его ставят на график ради разметки. Ветвь сообщения у него
     * молчит — выхода «сообщение» нет, и событие не случается никогда.
     */
    if (branches.length === 0)
        branches.push({ ...emptyBranch('ALERT'), trigger: happened('alert') });
    return {
        id: frame.id,
        name: frame.name,
        venue: frame.venue,
        symbol: frame.symbol,
        timeframe: frame.timeframe,
        branches,
        threshold: 0,
        version: 1,
        enabled: true,
        notify: false,
        createdAt: frame.now,
        updatedAt: frame.now,
        // «план …» в коде — план сделки индикатора: вкладка «Сделка», зоны на графике и боты берут его.
        ...(program.trade ? { trade: normalizeTradePlan({ ...DEFAULT_TRADE_PLAN, ...program.trade }) } : {}),
    };
}
/** Код и его настройки из описания индикатора — чтобы открыть его снова в редакторе. */
export function scriptOfSpec(spec) {
    for (const branch of spec.branches) {
        const left = branch.trigger?.left;
        if (left?.kind === 'indicator' && left.indicator === 'script' && typeof left.params['source'] === 'string') {
            return { source: left.params['source'], inputs: scriptInputsOf(left.params) };
        }
    }
    return null;
}
/** Слова языка — для подсветки и справочника в редакторе. */
export const SCRIPT_WORDS = {
    keywords: [...Object.keys(KEYWORDS), ...Object.keys(STATEMENTS), ...THEN, ...STEP_WORDS],
    prices: Object.keys(PRICES),
    functions: [...Object.keys(BUILTINS), ...Object.keys(FUNCTION_ALIASES), ...Object.keys(LIST_ALIASES), ...Object.keys(DRAW_ALIASES), ...Object.keys(SERIES_WORDS), ...SESSION_WORDS],
    colors: Object.keys(COLORS),
    constants: Object.keys(CONSTANTS),
};
/**
 * Какие чужие ряды нужны коду — чтобы терминал и сервер загрузили их
 * заранее. Вложенные «монета(…)» и «на(…)» складываются: монета ETH и
 * внутри неё 4H — это ETH на 4H.
 */
export function scriptRequestsOf(program) {
    const out = new Map();
    const walk = (expr, symbol, timeframe) => {
        if (expr.kind === 'on') {
            const next = { symbol: expr.symbol ?? symbol, timeframe: expr.timeframe ?? timeframe };
            out.set(`${next.symbol ?? ''}|${next.timeframe ?? ''}`, next);
            walk(expr.of, next.symbol, next.timeframe);
            return;
        }
        for (const child of childrenOf(expr))
            walk(child, symbol, timeframe);
    };
    const all = [
        ...program.assigns.map((entry) => entry.expr),
        ...program.plots.map((entry) => entry.expr),
        ...program.backgrounds.map((entry) => entry.expr),
        ...program.table.map((entry) => entry.expr),
        ...[program.buy, program.sell, program.alert?.expr ?? null].filter((entry) => entry !== null),
    ];
    walkSteps(program.steps, (expr) => all.push(expr));
    for (const fn of program.functions)
        walkSteps(fn.body, (expr) => all.push(expr));
    for (const expr of all)
        walk(expr, null, null);
    return [...out.values()];
}
/** То же по тексту кода — для загрузки рядов до расчёта. */
export function scriptRequestsOfSource(source, registry) {
    const parsed = compileScript(source, registry);
    return parsed.ok ? scriptRequestsOf(parsed.program) : [];
}
/** Код из TradingView: его можно перенести (`translatePine`), но не посчитать как есть. */
export function isPineScript(source) {
    return pineLine(source) > 0;
}
/**
 * Имена, которые в Коде ANZONIX заняты: слова языка, цены, постоянные,
 * функции и индикаторы. Перенос из Pine переименовывает такие имена
 * пользователя («buy» → «buy_»), иначе код не разобрался бы.
 */
export function isReservedScriptName(name, registry) {
    const lower = name.toLowerCase();
    return (lower in KEYWORDS ||
        lower in STATEMENTS ||
        lower in PRICES ||
        lower in CONSTANTS ||
        lower in BUILTINS ||
        lower in FUNCTION_ALIASES ||
        lower in LIST_ALIASES ||
        lower in DRAW_ALIASES ||
        lower in SERIES_WORDS ||
        SESSION_WORDS.has(lower) ||
        THEN.has(lower) ||
        STEP_WORDS.has(lower) ||
        registry.some((spec) => spec.id === lower));
}
//# sourceMappingURL=script.js.map